import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { useGetExportCombo } from '@/services/excel/useExcelQuries'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { invalidError, notice } from '@/components/pbo/notice'
import { HELPER_OPTION_TRUNCATED_MESSAGE } from '@/utils/consts'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { canSelectShipper, defaultShipper, scopedShipperCode, selectableShippers, ShipperScope, shipperScopeOf, withCoMingle } from '@/components/system/shipperScope'
import { useCoMingleShipperScope } from '@/components/system/useCoMingleShipperScope'
import { useFeatureFlag } from '@/services/feature/useFeatureQueries'
import { STOCK_LEDGERS_EXCEL_URL } from '@/services/inventory/stockLedger/apis'
import { buildStockLedgerPayload } from '@/services/inventory/stockLedger/payload'
import {
  useGetBrandNameOptions,
  useGetGoodsCodeOptions,
  useGetShipperOptions,
  useGetStockLedgers,
  useGetWarehouseNameOptions,
} from '@/services/inventory/stockLedger/useStockLedgerQueries'
import { StockLedgerFilter as StockLedgerFilterParams, StockLedgerView } from '@/services/inventory/stockLedger/types'
import {
  CLS_DATE_PARAM_FORMAT,
  EXCEL_ROW_LIMIT,
  FILTER_KEYS,
  MAX_SEARCH_DAYS,
  MESSAGES,
  REASON_DETAIL_MIN_LENGTH,
  SKU_ID_COLUMN_WIDTH,
  STOCK_LEDGER_SHIPPER_LOCK_AND_SKU_ID_FLAG,
  WAREHOUSE_ALL,
} from '@/pages/inventory/stockLedger/constants'
import { clearLastView, nextSearchSeq, saveLastView, startingView } from './lastView'
import InventoryHistoryPageView, { type ActionCtx, type Option, type Row, type Wiring } from './handoff/page.generated'
import type { core } from '@musinsa/pbo-core'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'
import { usePermissionSlot } from '@/hooks/useMenuPermission'

/*
 * ⚠ 이번 전환에서 **빠진 기능** — 옛 화면에 있었으나 데모에 없는 것. 데모가 기준이라 데모대로 간다.
 *
 *  옛 `components/StockLedgerTable.tsx` 는 그리드 본체로 ag-Grid 를 써서 두 가지를 제공했다:
 *   ① 그룹핑 패널 상시 노출(`rowGroupPanelShow="always"`) — 드래그로 묶는 5컬럼:
 *      화주코드(SPR_NM) · 상품군(GDS_GROUP1) · 브랜드명(BRD_NM) · 상품코드(GDS_CD) · 바코드(BARCODE)
 *   ② 그룹 합계 6종(`aggFunc: 'sum'`): BOH_QTY · NORM_INB_QTY · RSN_INB_QTY · NORM_OUB_QTY · RSN_OUB_QTY · EOH_QTY
 *      (조정수량 ADJ_QTY 만 합계 제외)
 *
 *  재고수불 **데모는 표 하나짜리 단순 구성**이고 그룹핑·합계가 없다. 그래서 이 화면에도 없다.
 *  그 결과 아래는 이 화면에서 더 이상 쓰이지 않는다(파일은 남지만 참조 0):
 *   - components/StockLedgerTable.tsx
 *   - components/columnDefinitions.ts (GROUPABLE_FIELDS · SUM_FIELDS)
 *   - 이 화면에서의 ag-grid-react · ag-grid-community 의존
 *
 *  되살려야 한다면 dev 코드를 되돌리는 게 아니라 **데모부터 바꾼다** —
 *  그룹핑 표를 디자인 요소로 정의 → 컨덕터 반영 → 재핸드오프. 그때 페이징 규약도 함께 정해야 한다
 *  (옛 구현이 ag-Grid 내장 페이징을 쓴 이유가 "그룹 행까지 함께 페이징돼야 그룹 합계가 안 깨진다" 였다).
 */

/**
 * 상품코드 헬퍼 후보 → 모달 표 행. 2.0 헬퍼는 (itemCode·brandName·itemName) 3컬럼을 주는데
 * 모달 표 컬럼은 화면 설계 키(BRD_NM·GDS_CD·GDS_NM·BARCODE)다 — 이름을 바꿔 넘기지 않으면
 * **표가 통째로 빈다**(고른 값도 빈 문자열로 인풋에 들어간다).
 * 바코드 컬럼은 2.0 헬퍼에 대응 값이 없다(바코드는 별도 헬퍼 소관) — 빈 칸으로 둔다.
 */
/**
 * `unknown` 값 → 화면 문자열. 조회영역·모달 폼 버스는 값을 `unknown` 으로 준다.
 *  `String(v ?? '')` 로 바로 찍으면 배열은 `"a,b"`, 객체는 `"[object Object]"` 가 **조용히** 실린다
 *  (moms-fe #803 Sonar S6551). 배열은 첫 칸을 쓰고(멀티 컨트롤 규약), 그 밖의 객체는 빈 값으로 둔다.
 */
const asText = (v: unknown): string => {
  const x = Array.isArray(v) ? v[0] : v
  return typeof x === 'string' || typeof x === 'number' ? String(x).trim() : ''
}

const toGoodsRow = (o: { itemCode: string | null; brandName: string | null; itemName: string | null }): Record<string, unknown> => ({
  ...o,
  BRD_NM: o.brandName ?? '',
  GDS_CD: o.itemCode ?? '',
  GDS_NM: o.itemName ?? '',
})

/** 'YY-MM-DD'·'YYYY-MM-DD' → dayjs. 생성 뷰는 날짜를 문자열로 보고한다(옛 조회영역은 dayjs 였다). */
const toDay = (raw: unknown) => {
  const s = String(raw ?? '').trim()
  if (!s) return null
  const d = dayjs(s.length === 8 ? `20${s}` : s)
  return d.isValid() ? d : null
}

/**
 * API 행(camelCase) → 표 행(UPPER_SNAKE). 생성 뷰의 컬럼은 화면 설계(데모)의 키를 쓴다 —
 * 변환 없이 넘기면 표가 통째로 빈다. 원본 키도 남긴다.
 */
const toViewRow = (r: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = { ...r }
  for (const [k, v] of Object.entries(r)) {
    out[k.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toUpperCase()] = v // totalQty → TOTAL_QTY
    // 밑줄 없는 컬럼도 있다(totalQty → TOTALQTY). 한 벌만 내보내면 그 컬럼이 통째로 빈다(2026-09-20 st02 실측).
    out[k.toUpperCase()] = v
  }
  return out
}

/**
 * 세션 → 이 화면의 화주 범위(기본 화주 · 바꿀 수 있는가 · 화주명). 첫 렌더에 한 번 부른다.
 *  컴포넌트 밖에 둬 화면 본체의 분기(인지 복잡도)를 늘리지 않는다.
 *  재고통합 ON 이면 두 분기 모두 잠긴 29CM·INOOINT 가 MUSINSA 도 고른다([withCoMingle], LOGISPRD-15610).
 */
const sessionShipperScope = (shipperLockAndSkuId: boolean, coMingle: boolean): ShipperScope => {
  const u = AuthStorage.user.get()
  if (shipperLockAndSkuId) {
    /*
     * LOGISPRD-14791 플래그 ON — 상품속성관리(LOGISPRD-14470 · 14650)와 같은 공용 판정. 화주 전환은 `shipper_code` 만 바꾸고
     *  `SPR_NM` 은 그대로라, `SPR_NM` 을 보면 MASTER 가 GLOWNY 로 전환해도 MUSINSA 로 열린다. `is_convertible` 도 보지 않는다 —
     *  전환 가능 계정이라도 지금 화주가 `FREE_SHIPPERS` 4종이 아니면 그 화주로 고정한다. 화주를 모르는 세션은 잠그지 않는다.
     */
    return withCoMingle(shipperScopeOf(u), coMingle)
  }
  // 화주전환 가능 내부 계정만 화주를 바꿀 수 있다 — 그 외는 자기 화주 고정(ST10/ST11 공통 근거).
  return withCoMingle({
    locked: !u.is_convertible,
    shipperCode: String(u.SPR_NM ?? ''),
    shipperName: String((u as { shipperName?: string }).shipperName ?? ''),
  }, coMingle)
}

/**
 * ST10 재고수불 — 1.0 ST03 을 신규 디자인(pbo-core)으로 재구현한 화면. (LOGISPRD-10666)
 *
 * 겉(조회영역·표·엑셀 사유 모달·서치헬퍼)은 컨덕터 뷰(`./handoff/page.generated`)가 그리고,
 * 이 파일은 **옛 훅·API·검증·세션/URL 유지를 그대로 두고** 그 뷰에 배선(Wiring)으로 넘긴다(컴포넌트 모드).
 *
 * ── UI 가 달라지며 생긴 차이 ──
 * · 조회영역 값이 dayjs 가 아니라 문자열로 온다 — 옛 validate/toFilter 와 **같은 규칙**을 여기서 적용한다.
 * · 서치헬퍼(브랜드·상품코드)는 뷰가 모달까지 그린다. 후보는 옛 훅 그대로 쓰고, 옛 모달의
 *   "모달 안 [조회] 를 눌러야 받는다" 게이트는 뷰가 주는 모달 열림(onModal)·적용 조건(ROWS2/ROWS3 filters)으로 복원했다.
 */
function StockLedger({ shipperLockAndSkuId, coMingle = false }: Readonly<{ shipperLockAndSkuId: boolean; coMingle?: boolean }>) {
  const menuSession = useMenuOnSession() // reload 를 구조분해하지 않는다(unbound-method)
  const { parsedQs, replaceQuery } = useParsedQuery<Record<string, unknown>>()

  const sessionShipperRef = useRef<string>()
  const canEditShipperRef = useRef<boolean>()
  const shipperNameRef = useRef<string>()
  const scopeRef = useRef<ShipperScope>()
  if (sessionShipperRef.current === undefined) {
    const scope = sessionShipperScope(shipperLockAndSkuId, coMingle)
    scopeRef.current = scope
    sessionShipperRef.current = scope.shipperCode
    canEditShipperRef.current = !scope.locked
    shipperNameRef.current = scope.shipperName
  }
  const scope = scopeRef.current as ShipperScope
  const forcedShipper = canEditShipperRef.current ? undefined : sessionShipperRef.current || undefined
  /**
   * 화주 고정 계정은 고를 수 있는 화주(자기 화주, 재고통합 ON 이면 MUSINSA)만 쓴다 — 그 밖의 값은 자기 화주로 덮는다.
   *  조회·초기값·서치헬퍼 세 곳을 같은 규칙으로 막는다. 고정이 아니면 받은 값 그대로다.
   */
  const pinShipper = useCallback((value: string): string => (forcedShipper ? scopedShipperCode(scope, value) : value), [forcedShipper, scope])

  /**
   * 조회영역 값(키 = filterKey) → BE 조회 필터. 옛 toStockLedgerFilter 와 같은 규칙.
   *  warn — 검증 실패 안내. 진입 조회 초기값은 렌더 중에 계산하므로 안내 없이 부른다.
   */
  const toFilter = useCallback(
    (filters: Record<string, unknown>, warn: (message: string) => void = notice.invalid): StockLedgerFilterParams | null => {
      const sprNm = pinShipper(asText(filters.SPR_NM))
      if (!sprNm) { warn(MESSAGES.shipperRequired); return null }
      const range = filters.st03ClsDate as unknown[] | undefined
      const from = Array.isArray(range) ? toDay(range[0]) : null
      const to = Array.isArray(range) ? toDay(range[1]) : null
      if (!from || !to) { warn(MESSAGES.clsDateRequired); return null }
      // 역전·기간 상한은 BE 검증을 선차단한다(옛 validateStockLedgerForm 과 같은 기준).
      if (from.isAfter(to, 'day')) { warn(MESSAGES.clsDateInvalidRange); return null }
      if (to.diff(from, 'day') + 1 > MAX_SEARCH_DAYS) { warn(MESSAGES.clsDatePeriodExceeded); return null }

      const brandName = asText(filters.BRD_NM)
      const goodsCode = asText(filters.GDS_CD)

      /*
       * ── UI 가 달라져 생긴 차이: 센터명 ──
       * 옛 조회영역은 센터명이 **단일 선택**이었고 API `WH_NM` 도 단건 EQUAL('ALL'/공백 = 전 센터)이다.
       * 새 디자인은 멀티셀렉트라 값이 `string[]` 로 올라온다('' = 전체 sentinel).
       *  · 0개 / '전체' 포함  → 'ALL'(전 센터) — 옛 동작 그대로.
       *  · 1개                → 그 센터 — 옛 동작 그대로.
       *  · 2개 이상           → **현재 API 가 못 받는다**. 조용히 첫 값만 보내면 사용자가 고른 나머지
       *    센터가 말없이 빠지므로(틀린 숫자를 맞는 것처럼 보여주는 쪽이 더 나쁘다) 막고 안내한다.
       *  TODO(api): BE 가 `WH_NM` 다건(IN)을 받게 되면 이 분기를 지우고 배열을 그대로 싣는다.
       *    행이 센터 합산(GROUP BY 에 WH_NM 없음)이라 화면단 필터로는 대신할 수 없다.
       */
      const whRaw = filters.WH_NM
      const whPicked = (Array.isArray(whRaw) ? whRaw : whRaw == null || whRaw === '' ? [] : [whRaw])
        .map((v) => String(v).trim())
        .filter((v) => v && v !== WAREHOUSE_ALL)
      if (whPicked.length > 1) { warn(MESSAGES.warehouseSingleOnly); return null }

      return {
        SPR_NM: sprNm,
        CLS_DATE: [from.format(CLS_DATE_PARAM_FORMAT), to.format(CLS_DATE_PARAM_FORMAT)],
        ...(brandName ? { BRD_NM: brandName } : {}),
        ...(goodsCode ? { GDS_CD: goodsCode } : {}),
        WH_NM: whPicked[0] ?? WAREHOUSE_ALL,
      }
    },
    [pinShipper],
  )

  /**
   * 남겨 둔 조회 조건(URL) — 조회영역 초기값(filterInitial)과 진입 조회가 같은 값을 쓴다. 비면 첫 진입이다.
   *  화주 고정 계정이면 URL 의 화주를 자기 화주로 덮는다(권한 밖 조회를 URL 로 뚫지 못하게).
   */
  const [restored] = useState(() => {
    // 조회영역 필드만 받는다 — 무관한 쿼리(?foo=bar)만 있는 URL 을 남겨 둔 조건으로 보면 화주 고정 계정은 진입 조회가 돈다.
    const r: Record<string, unknown> = Object.fromEntries(
      FILTER_KEYS.filter((k) => parsedQs[k] != null && parsedQs[k] !== '').map((k) => [k, parsedQs[k]]),
    )
    if (!Object.keys(r).length) return undefined
    if (forcedShipper) r.SPR_NM = pinShipper(asText(r.SPR_NM))
    // 다건 키는 **1건이면 문자열**로 돌아온다(qs comma 규약). 멀티셀렉트는 배열을 받아야 한다 —
    //  문자열이면 접힘 칩이 사라지고 '전체' 판정(value.includes(''))이 늘 참이 된다(#790 리뷰).
    if (r.WH_NM != null && !Array.isArray(r.WH_NM)) {
      r.WH_NM = [String(r.WH_NM)]
    }
    return r
  })
  /*
   * 보고 있는 조회 — 남겨 둔 조건이 [조회] 검증을 통과하면 그 조건으로 시작한다(LOGISPRD-14439). 같은 조건으로 돌아왔으면 지난 조회 순번·페이지를
   *  되살려 캐시된 페이지를 다시 받지 않고 보여 준다(LOGISPRD-14929). 조건이 없으면(첫 진입) 조회하지 않는다 — 필수 필터(화주·마감일자)를
   *  채우고 [조회]를 눌러야 실행된다.
   */
  const [start] = useState(() => startingView(restored ? toFilter(restored, () => undefined) : null))
  const [queryFilter, setQueryFilter] = useState(start?.filter ?? null)
  const [searchSeq, setSearchSeq] = useState(start?.seq ?? 0)
  /** 보는 페이지(1-base). 페이저가 소유한 값을 onState 로 받아 둔다 — 조회 key 가 아니라 재진입 때 보던 쪽을 되살리는 데만 쓴다. */
  const [page, setPage] = useState(start?.page ?? 1)
  /** 받는 중에 온 페이지 통지 — 받기가 끝나면 받는다(아래 flush effect, LOGISPRD-15349) */
  const pendingPageRef = useRef<number | null>(null)
  const view: StockLedgerView | null = useMemo(
    () => (queryFilter ? { filter: queryFilter, seq: searchSeq, page } : null),
    [queryFilter, searchSeq, page],
  )
  const { rows: items, totalCount, isFirstLoading, isFetchingRest, guardAction } = useGetStockLedgers(view)
  // 받기가 끝나면 받는 중 미뤄 둔 페이지를 받는다 — 끝난 렌더에서 뷰가 다시 알렸으면 onState 가 먼저 비워 두어 여기선 할 일이 없다(자식 effect 가 먼저 돈다).
  useEffect(() => {
    if (isFirstLoading || isFetchingRest || pendingPageRef.current == null) return
    setPage(pendingPageRef.current)
    pendingPageRef.current = null
  }, [isFirstLoading, isFetchingRest])
  // [초기화]가 비운 조회는 다시 저장하지 않는다 — 뷰가 페이저를 1쪽으로 돌리며 view 가 바뀌어 지난 조회를 되살리는 것을 막는다.
  const resetSeqRef = useRef<number>()
  useEffect(() => { if (view && view.seq !== resetSeqRef.current) saveLastView(view) }, [view])
  const { isLoading: isDownloading, onDownloadExcel } = useExcelDownload<ReturnType<typeof buildStockLedgerPayload>>({
    fileNamePrefix: '재고수불',
    config: { method: 'post' },
  })

  const { options: shipperOptions } = useGetShipperOptions()
  const { options: warehouseOptions } = useGetWarehouseNameOptions()
  /*
   * 센터명 옵션에서 콤보의 'ALL'(전체)을 뺀다 — 뷰의 MultiSelectBox 가 allOption 으로 자기 '전체'(값 '')를
   *  이미 붙인다. 안 빼면 드롭다운에 '전체'가 **두 개** 나오고, 콤보 ALL 과 센터를 함께 고르면 조회 조건이
   *  엇갈린다(옛 StockLedgerFilter 도 같은 이유로 걸러 냈다 — 2026-09-21 moms-fe #790 리뷰).
   */
  const whOptionsNoAll = useMemo(
    () => warehouseOptions.filter((o) => o.value !== WAREHOUSE_ALL) as Option[],
    [warehouseOptions],
  )

  /*
   * 서치헬퍼 후보 — 옛 훅 그대로. **모달이 열려 있고 그 안 [조회]를 눌렀을 때만** 받는다(옛
   *  components/StockLedgerFilter.tsx · 1.0 헬퍼 동치: 입력만으로는 받지 않는다). 뷰가 [조회]·엔터 때
   *  그 시점의 모달 값을 `wiring.rows.ROWS2/ROWS3` 의 filters 로 준다(__applied = 눌렀음 · 열 때·[초기화] 때 `{}`).
   *  두 후보 API 모두 모달 조건을 요청에 싣는다(브랜드 BRD_NM 부분일치 · 상품 brandName/itemName) —
   *  화면에서 다시 거를 필요가 없다.
   *  상품코드는 조건이 있어야만 부른다 — goods-codes 는 무페이징 distinct 라 대형 화주(MUSINSA)를 조건 없이
   *  조회하면 응답이 수십 MB 가 되어 브라우저가 멎는다(옛 구현 주석 · dev 실기동 확인).
   *  브랜드는 옛 구현이 "목록이 작아 게이트하지 않는다"며 빈 검색어 [조회]도 받았다 — 그대로 둔다.
   */
  const [helperOpen, setHelperOpen] = useState({ brd: false, gds: false })
  const [brdQuery, setBrdQuery] = useState({ applied: false, keyword: '' })
  const brdQueryRef = useRef(brdQuery)
  const [gdsQuery, setGdsQuery] = useState({ applied: false, brand: '', item: '' })
  const gdsQueryRef = useRef(gdsQuery)

  /*
   * 헬퍼 화주 — **화면 조회영역의 지금 값**을 따라간다(옛 구현은 폼 값을 봤다). queryFilter 는 [조회]를 눌러야
   *  갱신돼서, 화주를 바꾸고 조회 전에 돋보기를 누르면 옛 화주 후보가 나왔다.
   *  ⚠️ 모달 표(ROWS2/ROWS3)의 filters 는 이제 **모달 적용 조건**이라 거기엔 화주가 없다 — 조회영역 값은
   *   본 표 슬롯(ROWS)의 filters 로만 온다(뷰가 매 렌더 조회영역 상태를 그대로 준다).
   */
  const [screenShipper, setScreenShipper] = useState('')
  const screenShipperRef = useRef(screenShipper)
  const syncScreenShipper = useCallback((filters?: Record<string, unknown>) => {
    if (!filters) return
    const next = asText(filters.SPR_NM)
    if (next === screenShipperRef.current) return
    screenShipperRef.current = next
    queueMicrotask(() => setScreenShipper(next)) // 렌더 중 호출이라 다음 틱으로 미룬다
  }, [])

  const helperShipper = pinShipper(screenShipper || asText(queryFilter?.SPR_NM) || asText(parsedQs.SPR_NM))
  /** 알럿 판정은 마이크로태스크에서 한다 — 그 시점의 화주를 보도록 매 렌더 남긴다. */
  const helperShipperRef = useRef(helperShipper)
  helperShipperRef.current = helperShipper

  /** 마지막으로 본 적용 조건 객체 — [조회]를 누른 순간을 가린다(같은 값 재조회도 안내). */
  const brdAppliedObjRef = useRef<unknown>(undefined)
  /** 뷰가 매 렌더 주는 브랜드 모달 적용 조건을 상태로 올린다 — 바뀔 때만(렌더 중 setState 를 피해 마이크로태스크로). */
  const syncBrdQuery = useCallback((filters?: Record<string, unknown>) => {
    const next = { applied: Boolean(filters?.__applied), keyword: asText(filters?.BRD_NM) }
    // [조회]마다 뷰가 새 적용 조건 객체를 준다(상태라 다시 그려도 같은 객체) — 같은 값으로 다시 눌러도 다시 안내하려고 객체로 판정한다.
    const pressed = filters !== brdAppliedObjRef.current && next.applied
    brdAppliedObjRef.current = filters
    if (pressed) {
      queueMicrotask(() => {
        // 화주 없이 누르면 받지 않는다(아래 enabled) — 말없이 0건이면 고장처럼 보이므로 알린다.
        if (!helperShipperRef.current) notice.invalid(MESSAGES.shipperRequired)
      })
    }
    const cur = brdQueryRef.current
    if (next.applied === cur.applied && next.keyword === cur.keyword) return
    brdQueryRef.current = next
    queueMicrotask(() => setBrdQuery(next))
  }, [])
  /** 마지막으로 본 적용 조건 객체 — [조회]를 누른 순간을 가린다(같은 값 재조회도 안내). */
  const gdsAppliedObjRef = useRef<unknown>(undefined)
  /** 상품코드 모달 적용 조건 — 위와 같은 방식. */
  const syncGdsQuery = useCallback((filters?: Record<string, unknown>) => {
    const next = { applied: Boolean(filters?.__applied), brand: asText(filters?.BRD_NM), item: asText(filters?.GDS_NM) }
    // [조회]마다 뷰가 새 적용 조건 객체를 준다(상태라 다시 그려도 같은 객체) — 같은 값으로 다시 눌러도 다시 안내하려고 객체로 판정한다.
    const pressed = filters !== gdsAppliedObjRef.current && next.applied
    gdsAppliedObjRef.current = filters
    if (pressed) {
      queueMicrotask(() => {
        // 조건 없이 누르면 받지 않는다(아래 enabled · 무페이징 전량 금지) — 옛 handleGoodsHelperSearch 와 같은 안내.
        if (!next.brand && !next.item) notice.invalid(MESSAGES.goodsHelperFilterRequired)
        else if (!helperShipperRef.current) notice.invalid(MESSAGES.shipperRequired)
      })
    }
    const cur = gdsQueryRef.current
    if (next.applied === cur.applied && next.brand === cur.brand && next.item === cur.item) return
    gdsQueryRef.current = next
    queueMicrotask(() => setGdsQuery(next))
  }, [])

  /** 브랜드 후보 — 모달 [조회] 때의 브랜드명으로 좁힌다(부분일치, 비우면 화주 전체 브랜드 — 옛 동작). */
  const brandHelperEnabled = Boolean(helperShipper) && helperOpen.brd && brdQuery.applied
  const { items: brandOptions, isTruncated: isBrandTruncated, dataUpdatedAt: brandUpdatedAt } = useGetBrandNameOptions(
    { shipperCode: helperShipper, brandName: brdQuery.keyword || undefined },
    brandHelperEnabled,
  )
  // 2.0 헬퍼는 1,000행에서 자른다 — 모달 [조회]로 받은 결과가 상한에 닿았을 때 알린다.
  //  게이트를 함께 보는 이유: 모달을 닫으면 적용 조건이 비워져 queryKey 가 캐시된 '조건 없음' 키로 돌아가는데,
  //  v5 는 disabled 여도 캐시 data 를 돌려줘 닫힌 화면에 안내가 뜬다. 다시 열고 [조회]하면 게이트가 켜져 다시 알린다.
  useEffect(() => {
    if (brandHelperEnabled && isBrandTruncated) notice.failure(HELPER_OPTION_TRUNCATED_MESSAGE)
  }, [brandHelperEnabled, isBrandTruncated, brandUpdatedAt])
  // **켜는 조건 = 실제로 요청에 실리는 값**이어야 한다. BE(GetGoodsCodeOptionsReq)는 shipperCode·brandName·itemName
  //  만 받고 상품코드 필드가 없다 — 다른 키로 켜면 좁히는 값 없이 전체 distinct 가 나가 막으려던 일이 그대로 난다
  //  (2026-09-21 moms-fe #790 리뷰 Critical).
  //  옛 구현은 `(brandName || itemName)` 로 게이트하고 둘 다 요청에 실었다 — 상품명만으로도 찾을 수 있어야 한다.
  const { items: goodsOptions } = useGetGoodsCodeOptions(
    { shipperCode: helperShipper, brandName: gdsQuery.brand || undefined, itemName: gdsQuery.item || undefined },
    Boolean(helperShipper) && (Boolean(gdsQuery.brand) || Boolean(gdsQuery.item)) && helperOpen.gds && gdsQuery.applied,
  )

  // 사유코드 콤보 — 옛 모달이 쓰던 공용 콤보 그대로(코드/명 배열이 짝을 이룬다. 명이 비면 코드로 대체).
  const { data: exportCombo } = useGetExportCombo({ errorToast: true })
  const reasonOptions = useMemo(
    () =>
      (exportCombo?.DOWNLOAD_CD?.COMBO_OPT_CD ?? []).map((code: string, idx: number) => ({
        value: code,
        label: exportCombo?.DOWNLOAD_CD?.COMBO_OPT_NM?.[idx] || code,
      })),
    [exportCombo],
  )

  const viewRows = useMemo(() => items.map((r) => toViewRow(r as unknown as Record<string, unknown>)) as unknown as Row[], [items])

  const wiring: Wiring = {
    permission: usePermissionSlot('st10'),
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { st03Table: coreTableLayoutSlot('ST10_HEAD') },
    rows: {
      /*
       * 전량 조회 — 받은 행을 total 없이 주면 뷰가 페이저 크기(1만)로 자르고, 목록 검색도 받은 전량에 건다. '전체 N' 은 받는 만큼 늘어난다(LOGISPRD-15270).
       *  첫 구간·나머지 구간·재조회 중에는 받은 행 위에 로더를 띄워 표를 막는다(LOGISPRD-15390).
       */
      // filters = 조회영역의 지금 값 — 서치헬퍼 화주의 출처로 읽기만 한다(위 syncScreenShipper).
      ROWS: (_demo, q) => { syncScreenShipper(q?.filters); return { rows: viewRows, loading: isFirstLoading || isFetchingRest } },
      // 서치헬퍼 후보 표 — 옛 훅이 주는 목록 그대로(모달 표는 뷰가 그린다). filters = 모달 [조회] 적용 조건.
      // 브랜드 후보는 문자열 목록이라 표 행으로 감싼다(상품코드는 객체 그대로).
      ROWS2: (_demo, q) => { syncBrdQuery(q?.filters); return brandOptions.map((name: string) => ({ BRD_NM: name })) as unknown as Row[] },
      ROWS3: (_demo, q) => { syncGdsQuery(q?.filters); return goodsOptions.map(toGoodsRow) as unknown as Row[] },
    },
    actions: {
      /** [조회] — 옛 handleSearch 그대로: 검증 → 세션·URL 저장 → 조건 커밋. 받는 것은 조건이 바뀐 query 가 한다(조회 순번이 올라 새로 받는다). */
      search: (filters) => {
        const filter = toFilter(filters)
        // 검증 실패 — 알럿은 toFilter 가 띄웠다. false 를 돌려 뷰가 페이저·목록 검색어를 1쪽·빈 값으로 되돌리지 않게 한다(새 조회가 없다).
        if (!filter) return false

        // 마지막 조회 조건을 메뉴 세션·URL 에 남긴다(다른 메뉴 다녀와도 유지).
        const stored = { ...filters, SPR_NM: filter.SPR_NM }
        MenuStorage.menuOnSession.setSearch(stored)
        menuSession.reload()
        replaceQuery(stored)

        // [조회]마다 조회 순번을 올려 같은 조건도 새로 받는다. 1페이지부터 본다(뷰의 페이저도 이어서 1페이지로 돌린다).
        setQueryFilter(filter)
        setSearchSeq(nextSearchSeq())
        setPage(1)
        pendingPageRef.current = null
      },
      /** 엑셀 — 화면에 떠 있는 결과와 **같은 조건**으로 받는다(폼 현재값이 아니라 마지막 조회 조건). */
      // 확인 슬롯은 **반환값이 없다(void)** — 생성 뷰가 토스트·닫기를 한다. 예전엔 `false` 를 돌려 토스트를
      //  막았는데, 이 화면 모달엔 토스트 문구 자체가 없어 **필요 없는 값**이었다(골격 wiring.skeleton.ts 도
      //  `async () => {}` 를 정본으로 준다). false 를 남겨 두면 생성본이 그 값을 '실패' 로 읽게 바뀌는 순간
      //  모달이 안 닫힌다 — 2026-09-21 에 실제로 그렇게 깨졌다. 실패는 throw 로만 알린다.

      st03DownloadModal_ok: async (ctx): Promise<void> => {
        const reasonCode = typeof ctx.form?.reason === 'string' ? ctx.form.reason : ''
        const reasonDetail = typeof ctx.form?.detail === 'string' ? ctx.form.detail.trim() : ''
        // 사유 필수·상세사유 최소 길이 — 옛 ExcelDownloadReasonModal 의 okDisabled 검증을 그대로 옮겼다.
        //  뷰의 [확인]은 항상 눌리므로(모달 버튼 잠금 슬롯이 없다) 여기서 막고 throw 해 모달을 열어 둔다.
        //  모달 안내 문구("5글자 이상 입력해주세요.")와 같은 상수를 본다 — 문구와 검증이 갈라지지 않게.
        if (!reasonCode) { throw invalidError(MESSAGES.excelReasonRequired) }
        if (reasonDetail.length < REASON_DETAIL_MIN_LENGTH) {
          throw invalidError(MESSAGES.excelReasonDetailTooShort)
        }
        if (!queryFilter) { throw invalidError(MESSAGES.excelNoRows) }
        onDownloadExcel(
          STOCK_LEDGERS_EXCEL_URL,
          buildStockLedgerPayload(queryFilter, AuthStorage.user.get().email ?? '', { reasonCode, reasonDetail }),
        )
      },
      // 서치헬퍼 [확인] — 고른 값을 인풋에 넣는 일은 뷰가 한다.
      st03BrdSearchModal_ok: () => {},
      st03GdsSearchModal_ok: () => {},
    },
    options: {
      /**
       * 화주 목록 — 화주전환 가능 계정만 전체를 본다(그 외는 고를 수 있는 화주만 — 옛 필터의 disabled 대체).
       * 로그인 화주가 목록에 없어도(내부 운영 계정) 기본값이 보이도록 보강한다.
       * 재고통합 ON 의 29CM·INOOINT 는 자기 화주와 MUSINSA 둘이다(LOGISPRD-15610).
       */
      ST03SHIPPER_OPTIONS: () => {
        const mine = sessionShipperRef.current ?? ''
        if (canEditShipperRef.current) {
          return (mine && !shipperOptions.some((o) => String(o.value) === mine)
            ? [{ value: mine, label: shipperNameRef.current || mine }, ...shipperOptions]
            : shipperOptions) as Option[]
        }
        return (mine ? selectableShippers(scope) : []).map((s) =>
          shipperOptions.find((o) => String(o.value) === s.shipperCode) ?? { value: s.shipperCode, label: s.shipperName || s.shipperCode },
        ) as Option[]
      },
      ST03WHNM_OPTIONS: () => whOptionsNoAll,
      /**
       * 사유코드 — 1.0 공용 콤보(DOWNLOAD_CD). 데모 옵션(업무활용·고객요청…)은 화면 설계용 예시라
       * 실제 감사로그 코드와 다르다. 콤보가 비면(로딩·실패) 데모 옵션 대신 **빈 목록**을 준다 —
       * 없는 코드를 고르게 두는 쪽이 더 나쁘다.
       */
      REASONRADIO_OPTIONS: () => reasonOptions as Option[],
    },
    /** 조회영역 기본값 — 화주는 기본 화주([defaultShipper], 재고통합 대상 계정은 MUSINSA, LOGISPRD-15719), 마감일자는 오늘~오늘(옛 buildDefaultValues). */
    filterDefaults: {
      st03FilterCard: (demo) => ({
        ...demo,
        SPR_NM: defaultShipper(scope).shipperCode || undefined,
        st03ClsDate: [dayjs().format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
        // 센터명 멀티셀렉트의 '전체'는 **빈 문자열 한 칸**이다(스칼라 'ALL' 을 주면 옵션에 없어 빈 칸으로 보인다).
        WH_NM: [''],
      }),
    },
    /** 진입 초기값 — 남겨 둔 조회 조건(위 restored)이 기본값을 덮는다. 화주 고정 계정이면 URL 복원값이 화주를 덮지 못한다. */
    filterInitial: {
      st03FilterCard: (defaults) => ({ ...defaults, ...restored }),
    },
    useBusy: () => isDownloading,
    /**
     * 화주명 잠금 — 플래그 ON 이고 화주가 고정이면 셀렉트를 비활성으로 그린다(옵션도 자기 화주 하나다). OFF 면 종전처럼 잠그지 않는다.
     *  재고통합 ON 의 29CM·INOOINT 는 자기 화주와 MUSINSA 를 고르므로 잠그지 않는다(LOGISPRD-15610).
     */
    locks: { st03Shipper: () => shipperLockAndSkuId && !canSelectShipper(scope) },
    /** 표 컬럼 — 플래그 ON 이면 바코드 뒤에 SKU ID 열을 둔다(LOGISPRD-14791). OFF 면 데모 컬럼 그대로. */
    columns: { ROWS: (demo) => (shipperLockAndSkuId ? withSkuIdColumn(demo) : demo) },
    opens: {
      /** 엑셀 — 결과가 있어야 하고 상한을 넘으면 안 된다(옛 handleDownloadClick). */
      st03ExcelButton: () => {
        if (!guardAction()) return false
        if (!queryFilter || items.length === 0) { notice.invalid(MESSAGES.excelNoRows); return false }
        // BE MAX_EXCEL_ROW_SIZE(100,000) count 선검사와 같은 기준으로 선차단한다.
        if (totalCount > EXCEL_ROW_LIMIT) { notice.invalid(MESSAGES.excelRowLimitExceeded); return false }
        return true
      },
    },
    /** 서치헬퍼 모달 열림 — 후보 조회를 이 신호로 게이트한다(옛 isXxxHelperOpen). */
    onModal: (open) => {
      setHelperOpen((st) =>
        st.brd === open.st03BrdSearchModal && st.gds === open.st03GdsSearchModal
          ? st
          : { brd: open.st03BrdSearchModal, gds: open.st03GdsSearchModal },
      )
      // 닫히면 그 모달의 적용 조건을 비운다 — 남겨 두면 다시 열 때 뷰가 빈 조건을 주기 전 한 렌더 동안
      //  '열림 + 지난 [조회] 조건'이 되어 후보 요청이 한 번 나간다(#861 리뷰).
      if (!open.st03BrdSearchModal && brdQueryRef.current.applied) { brdQueryRef.current = { applied: false, keyword: '' }; setBrdQuery({ applied: false, keyword: '' }) }
      if (!open.st03GdsSearchModal && gdsQueryRef.current.applied) { gdsQueryRef.current = { applied: false, brand: '', item: '' }; setGdsQuery({ applied: false, brand: '', item: '' }) }
    },
    /** 페이저 진입 초기값 — 쪽은 지난 조회에서 되살린 값(LOGISPRD-14929). 크기는 뷰 기본값(1만)이다. */
    stateInitial: { pager: { pager: { page } } },
    /**
     * 페이지 이동 — 뷰의 페이저가 소유한 쪽을 받아 둔다(재진입 복원용).
     * 받는 중에는 미룬다 — 덜 온 행으로 뷰가 마지막 페이지로 줄여 알린 값이 복원 페이지를 덮는다(LOGISPRD-15349).
     *  받는 중 사용자가 옮긴 페이지도 여기로 오므로 버리지 않고 마지막 값을 두었다가 받기가 끝나면 받는다.
     */
    onState: (st) => {
      if (isFirstLoading || isFetchingRest) {
        pendingPageRef.current = st.pager.pager.page
        return
      }
      pendingPageRef.current = null
      setPage(st.pager.pager.page)
    },
    /** [초기화] — 저장된 조건도 함께 비워 다음 진입이 기본값으로 열리게 한다(조회는 트리거하지 않는다). */
    onReset: () => {
      pendingPageRef.current = null
      MenuStorage.menuOnSession.setSearch({})
      // 탭바는 reload 로만 새 조건을 읽는다 — 빼면 탭 링크에 지운 조건이 남아 복귀 시 진입 조회가 그 조건으로 돈다.
      menuSession.reload()
      replaceQuery({})
      resetSeqRef.current = searchSeq
      clearLastView() // 다음 진입이 지난 조회를 되살리지 않게 한다
    },
  }

  return <InventoryHistoryPageView wiring={wiring} />
}

const SKU_ID_COLUMN: core.CoreColumnType = { title: 'SKU ID', width: SKU_ID_COLUMN_WIDTH, dataIndex: 'SKU_ID' }
/** 데모 컬럼 → 바코드 바로 뒤에 SKU ID 열(BE 응답 `SKU_ID`). 같은 입력엔 같은 배열을 돌려 표가 매 렌더 컬럼을 새로 받지 않게 한다. */
const skuIdColumnsCache = new WeakMap<core.CoreColumnType[], core.CoreColumnType[]>()
const withSkuIdColumn = (demo: core.CoreColumnType[]): core.CoreColumnType[] => {
  const cached = skuIdColumnsCache.get(demo)
  if (cached) return cached
  const at = demo.findIndex((c) => c.dataIndex === 'BARCODE')
  // 바코드 열이 없으면(재핸드오프로 열이 바뀐 경우) 끝에 붙인다 — 열을 잃지 않는다
  const next = at < 0 ? [...demo, SKU_ID_COLUMN] : [...demo.slice(0, at + 1), SKU_ID_COLUMN, ...demo.slice(at + 1)]
  skuIdColumnsCache.set(demo, next)
  return next
}

/**
 * 진입 시 LOGISPRD-14791 플래그를 한 번 정해(latch) 그린다. 화면은 첫 렌더에 화주 범위를 ref·초기 상태(URL 복원·진입 조회)에
 *  고정하므로 플래그가 늦게 오거나 머무는 동안 바뀌면 잠금이 반쯤만 걸린다 — 판정 전에는 그리지 않고, 첫 판정값을 고정한다.
 *  재조회로 값이 바뀌어도 이 화면은 첫 값 그대로다(다음 진입부터 새 값). allocationPriorityV2 와 같은 방식이다.
 *  조회 실패는 OFF(종전 화면)다 — getFeatureFlag 가 실패를 false 로 돌려준다. data 가 비어도(방어) OFF 로 본다.
 */
function StockLedgerEntry() {
  const { data: enabled, isPending } = useFeatureFlag(STOCK_LEDGER_SHIPPER_LOCK_AND_SKU_ID_FLAG)
  // 재고통합 FF 도 진입 시점 값으로 정해진 뒤에 그린다 — 화주 범위를 첫 렌더에 고정하므로(LOGISPRD-15610)
  const coMingle = useCoMingleShipperScope()
  const [latched, setLatched] = useState<boolean | null>(null)
  // 렌더 중 setState — React 가 이 렌더(null)를 버리고 곧바로 다시 렌더한다(공식 '렌더 중 상태 갱신' 패턴).
  //  첫 판정 직후 한 번만 타고(latched 가 더는 null 이 아니다), 두 번째 렌더부터 화면을 그린다.
  //  선례: src/pages/outbound/allocationPriorityV2/index.tsx FlaggedAllocationPriorityScreen
  if (latched === null) {
    if (!isPending) setLatched(enabled === true)
    return null
  }
  if (!coMingle.ready) return <div role="status">수불부를 준비하고 있어요.</div>
  return <StockLedger shipperLockAndSkuId={latched} coMingle={coMingle.coMingle} />
}

export default StockLedgerEntry
